import { PERSONAS } from '@moksy/common/src/permissions/personas.js';
import { Text } from '../elements/text.element';

/**
 * 🎭 The hat switcher — YOUR identity, a different experience.
 *
 * Replaces the old raw-role list. That list only appeared when an account
 * held more than one role, which meant a super admin — who holds exactly one
 * — could not switch at all, and the only way to see the platform as a
 * customer was to log out. Personas are derived rather than stored, so that
 * account now gets a real Customer ↔ Staff choice with no role edits.
 *
 * A persona the account may NOT wear is still rendered, disabled, carrying
 * the reason. The case that matters is a reviewer looking for the customer
 * hat: the platform withholds it on purpose (see reviewerSeparation.js), and
 * an absent control would read as a broken feature rather than a policy.
 *
 * This is not impersonation and not the staff preview — see
 * staff-view-as.block.jsx, kept separate so the two are not confused.
 */

const PERSONA_META = {
  [PERSONAS.CUSTOMER]: {
    blurb: 'Your own ideas, projects and reports.',
    dot: 'bg-blue-500'
  },
  [PERSONAS.REVIEWER]: {
    blurb: 'Assess other people’s work. Earnings and standing.',
    dot: 'bg-indigo-500'
  },
  [PERSONAS.STAFF]: {
    blurb: 'Operate the platform. Admin consoles.',
    dot: 'bg-purple-500'
  }
};

export const PersonaSwitcher = ({
  options = [], activePersona, onSwitch, onDone,
  rolesInPersona = [], activeRole, onSwitchRole, roleLabel = (r) => r
}) => {
  // One option is not a choice. Rendering a single-item switcher would imply
  // there is somewhere else to go.
  //
  // "Somewhere else" includes a second ROLE inside the one hat: an account
  // holding `user` and `designer` has a single persona but two navigations,
  // and returning early on the persona count alone would hide that choice —
  // the exact capability the sub-role chips exist to preserve.
  const selectable = options.filter((o) => o.available);
  const hasBlocked = options.some((o) => !o.available);
  if (selectable.length < 2 && !hasBlocked && rolesInPersona.length < 2) return null;

  return (
    <div className="px-2 pb-1 border-b border-gray-100 mb-1">
      <Text variant="label" className="px-2 pt-2 pb-1 block uppercase tracking-wider text-[10px] text-gray-400">
        Working as
      </Text>

      {/* With a single hat the persona rows are noise — there is nothing to
          pick — so only the role chips below are worth showing. */}
      {(selectable.length > 1 || hasBlocked) && options.map((option) => {
        const meta = PERSONA_META[option.persona] || {};
        const isActive = option.persona === activePersona;

        if (!option.available) {
          return (
            <div
              key={option.persona}
              className="px-3 py-1.5 rounded-xl opacity-60 cursor-not-allowed"
              title={option.reason || undefined}
            >
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0 border-2 border-gray-200 bg-background" aria-hidden="true" />
                <span className="flex-1 text-sm text-gray-400 line-through">{option.label}</span>
                <span className="text-[10px] text-gray-400">unavailable</span>
              </div>
              {/* The reason, not just the refusal. Somebody hunting for a
                  missing control needs to learn it was withheld deliberately. */}
              {option.reason && (
                <p className="mt-1 pl-[18px] pr-1 text-[10px] leading-snug text-gray-500">
                  {option.reason}
                </p>
              )}
            </div>
          );
        }

        return (
          <button
            key={option.persona}
            type="button"
            onClick={() => { onSwitch?.(option.persona); onDone?.(); }}
            role="menuitemradio"
            aria-checked={isActive}
            className={`w-full text-left px-3 py-1.5 rounded-xl transition-colors ${
              isActive ? 'bg-gray-50' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 border-2 ${
                  isActive ? `${meta.dot || 'bg-gray-400'} border-current` : 'border-gray-300 bg-background'
                }`}
                aria-hidden="true"
              />
              <span className={`flex-1 text-sm ${isActive ? 'font-semibold text-neutral' : 'text-gray-600'}`}>
                {option.label}
              </span>
              {isActive && <span className="text-[10px] text-gray-400 font-normal">active</span>}
            </div>
            {meta.blurb && (
              <p className="mt-0.5 pl-[18px] pr-1 text-[10px] leading-snug text-gray-400">
                {meta.blurb}
              </p>
            )}
            {/* ⚠️ A hat you CAN wear that still withholds something.
                A reviewer holds the customer hat — their own projects,
                invoices and downloads are all theirs to see — but cannot
                START new work, because reviewing your own output is the
                conflict `reviewerSeparation` exists to prevent.
                Said here, where the choice is made, rather than left as a
                missing "+ New project" button somebody hunts for. */}
            {option.constraint && (
              <p className="mt-0.5 pl-[18px] pr-1 text-[10px] leading-snug text-amber-700">
                {option.constraint}
              </p>
            )}
          </button>
        );
      })}

      {/* Roles WITHIN the active hat. Only shown when there is more than one,
          because a single-entry list is not a choice — and only for the active
          hat, since a role is meaningless outside the persona that contains
          it. This is what keeps a `user` + `designer` account able to pick its
          navigation now that the raw-role list is gone. */}
      {rolesInPersona.length > 1 && (
        <div className="mt-1 pl-[18px] pr-1">
          <Text variant="label" className="pb-1 block uppercase tracking-wider text-[10px] text-gray-400">
            as
          </Text>
          <div className="flex flex-wrap gap-1 pb-1">
            {rolesInPersona.map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => { onSwitchRole?.(role); onDone?.(); }}
                aria-pressed={role === activeRole}
                className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
                  role === activeRole
                    ? 'border-gray-400 bg-gray-100 font-semibold text-neutral'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {roleLabel(role)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonaSwitcher;
